import React, { useState, useEffect } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { 
  List, 
  Card, 
  Space, 
  Typography, 
  Table, 
  Tag, 
  Button, 
  Popconfirm, 
  Alert, 
  Empty, 
  Spin,
  InputNumber,
  Checkbox,
  Form
} from "@pankod/refine-antd";
import { 
  EyeInvisibleOutlined, 
  SearchOutlined, 
  FilterOutlined,
  PictureOutlined,
  DollarCircleOutlined
} from "@ant-design/icons";
import { findVisibilityConflicts, deactivateVisibility } from "Services/productServices";
import toast from "react-hot-toast";

const { Text, Paragraph } = Typography;

export const VisibilityManagement: React.FC<IResourceComponentsProps> = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    minPrice: 0,
    maxPrice: 10,
    noImage: true
  });

  const handleScan = async () => {
    setLoading(true);
    setProducts([]);
    setSelectedIds([]);
    try {
      const response = await findVisibilityConflicts(filters);
      if (response.data.success) {
        setProducts(response.data.products);
        if (response.data.products.length === 0) {
          toast.success("No products found matching these criteria.");
        } else {
          toast.success(`Found ${response.data.products.length} products`);
        }
      } else {
        toast.error(response.data.error || "Failed to fetch products");
      }
    } catch (error) {
      console.error("Visibility scan failed:", error);
      toast.error("Error connecting to bridge");
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (selectedIds.length === 0) {
      toast.error("No products selected");
      return;
    }

    setDeactivating(true);
    try {
      const response = await deactivateVisibility(selectedIds);
      if (response.data.success) {
        toast.success(`Successfully deactivated ${response.data.summary.deactivatedCount} products`);
        handleScan();
      } else {
        toast.error("Deactivation failed");
      }
    } catch (error) {
      console.error("Deactivation failed:", error);
      toast.error("Error during deactivation");
    } finally {
      setDeactivating(false);
    }
  };

  const selectAll = () => {
    setSelectedIds(products.map(p => p.id));
  };

  const columns = [
    {
      title: "Master SKU",
      dataIndex: "masterSku",
      key: "masterSku",
      render: (sku: string) => <Text strong>{sku}</Text>
    },
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (val: number) => `$${val.toFixed(2)}`,
      sorter: (a: any, b: any) => a.price - b.price
    },
    {
      title: "Stock",
      dataIndex: "stock",
      key: "stock",
      render: (val: number) => <Text strong>{val}</Text>,
      sorter: (a: any, b: any) => a.stock - b.stock
    },
    {
      title: "Image",
      dataIndex: "hasImage",
      key: "hasImage",
      render: (has: boolean) => has ? <Tag color="success">YES</Tag> : <Tag color="error">NO IMAGE</Tag>
    },
    {
      title: "Storefront Status",
      dataIndex: "isSearchable",
      key: "isSearchable",
      render: (searchable: boolean) => searchable ? <Tag color="blue">SEARCHABLE</Tag> : <Tag color="default">HIDDEN</Tag>
    }
  ];

  return (
    <List 
      title="Product Visibility Management"
      headerProps={{
        extra: (
          <Space>
            {products.length > 0 && (
              <>
                <Button onClick={selectAll}>Select All Results</Button>
                <Popconfirm
                  title={`Are you sure you want to HIDE ${selectedIds.length} products from search?`}
                  onConfirm={handleDeactivate}
                  okText="Yes, Hide Them"
                  cancelText="No"
                  okButtonProps={{ danger: true, loading: deactivating }}
                >
                  <Button 
                    danger 
                    type="primary"
                    icon={<EyeInvisibleOutlined />} 
                    disabled={selectedIds.length === 0}
                  >
                    Set to Unsearchable ({selectedIds.length})
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        )
      }}
    >
      <Alert
        message="Search Visibility Cleanup"
        description={
          <Paragraph>
            This tool helps you identify low-priced or incomplete products and remove them from Meilisearch results. 
            Products will remain in Strapi but will have their <strong>IsSearchableProduct</strong> flag set to false.
          </Paragraph>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card title={<span><FilterOutlined /> Filters</span>} style={{ marginBottom: 24 }}>
        <Form layout="inline" onFinish={handleScan}>
          <Form.Item label={<span><DollarCircleOutlined /> Min Price</span>}>
            <InputNumber 
              value={filters.minPrice} 
              onChange={(val) => setFilters(f => ({ ...f, minPrice: val || 0 }))} 
              precision={2}
            />
          </Form.Item>
          <Form.Item label={<span><DollarCircleOutlined /> Max Price</span>}>
            <InputNumber 
              value={filters.maxPrice} 
              onChange={(val) => setFilters(f => ({ ...f, maxPrice: val || 0 }))} 
              precision={2}
            />
          </Form.Item>
          <Form.Item>
            <Checkbox 
              checked={filters.noImage} 
              onChange={(e) => setFilters(f => ({ ...f, noImage: e.target.checked }))}
            >
              <PictureOutlined /> Only Missing Images
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleScan} loading={loading}>
              Apply Filters
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Scanning CMS for visibility matches..." />
          </div>
        ) : products.length > 0 ? (
          <Table 
            dataSource={products} 
            columns={columns} 
            rowKey="id"
            rowSelection={{
              selectedRowKeys: selectedIds,
              onChange: (keys: any) => setSelectedIds(keys)
            }}
            pagination={{ pageSize: 50 }}
          />
        ) : (
          <Empty description="No matches found. Try adjusting the filters." />
        )}
      </Card>
    </List>
  );
};
